import { beginLoading, endLoading } from '@app/AppLayout/LoadingContext';
import { useNotification } from '@app/AppLayout/NotificationContext';
import { CommunicationApi } from '@app/api';
import { DataLoadErrorRow } from '@app/components/DataLoadError';
import { PageActionsMenu } from '@app/components/PageActionsMenu';
import { RemoveButton } from '@app/components/RemoveButton';
import { useBreadcrumbLabel } from '@app/hooks/useBreadcrumbLabel';
import { useCommunications } from '@app/hooks/useCommunications';
import { useTablePreferences } from '@app/hooks/useTablePreferences';
import {
  Communication,
  RecipientSourceInfo,
  formatRecipientToken,
  isOverridden,
  parseRecipientList
} from '@app/model/Communication';
import { NOTIFICATION_SEVERITY } from '@app/model/Enums';
import { useCommunicationStore } from '@app/store/communicationStore';
import { toApiError } from '@app/utils/ApiError';
import { hasRealmRole } from '@app/utils/keycloak';
import { ROLE } from '@app/utils/roles';
import {
  ActionList,
  ActionListItem,
  Alert,
  AlertVariant,
  Bullseye,
  Button,
  Card,
  CardBody,
  Checkbox,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Icon,
  Label,
  LabelGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  PageSection,
  Pagination,
  PaginationVariant,
  SearchInput,
  Split,
  SplitItem,
  Title,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip
} from '@patternfly/react-core';
import {
  BellIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  HistoryIcon,
  LockIcon,
  PencilAltIcon,
  RedoAltIcon,
  SearchIcon
} from '@patternfly/react-icons';
import { ExpandableRowContent, InnerScrollContainer, OuterScrollContainer, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import * as React from 'react';

import { CommunicationEditModal } from './CommunicationEditModal';
import { CommunicationHistoryModal } from './CommunicationHistoryModal';
import { CommunicationPreviewModal } from './CommunicationPreviewModal';

/** How loud a communication is, coloured the way the rest of the app colours a severity. */
const SEVERITY_COLORS: Record<NOTIFICATION_SEVERITY, 'red' | 'orange' | 'blue'> = {
  [NOTIFICATION_SEVERITY.ERROR]: 'red',
  [NOTIFICATION_SEVERITY.WARNING]: 'orange',
  [NOTIFICATION_SEVERITY.INFO]: 'blue'
};

/**
 * Every message the service can send, and the control an admin has over it without a redeploy, at
 * /communications.
 *
 * Dynamic on purpose, exactly as General Settings is: every row, the placeholders its templates
 * may use, and the sources its recipients may come from are read from the service's catalog, so a
 * communication added there appears here and is editable with no change to this page. Nothing
 * about any individual message is named in this file.
 *
 * Not an entity store either, and for the same reason: the list is the catalog rather than a
 * table, so a communication nobody has overridden has no row and therefore no id. `name` is the
 * identity throughout.
 *
 * Reading is open to any authenticated user, so this page is legible to everybody and editable by
 * admins — which matters because "why did that email not arrive" is answered here, and it is not
 * only administrators who ask it.
 */
const Communications: React.FunctionComponent = () => {
  const { addNotification } = useNotification();

  useBreadcrumbLabel('Communications');

  /** Forced: this page has to show what the service is using right now rather than what the
   * cache last heard. */
  const { communications, error, retry } = useCommunications(true);
  const applyWrite = useCommunicationStore(state => state.applyWrite);

  // Writing needs the admin role — every method other than the three reads is admin-only. Only
  // decides what to offer; the service enforces it regardless.
  const isAdmin = hasRealmRole(ROLE.ADMIN);

  /**
   * The sources a recipient may come from, read once for the editor's picker. Read from the
   * service rather than assembled here so the picker cannot offer something a PUT would reject.
   *
   * Never able to fail the page: without it the editor validates nothing locally and the service
   * still answers, which is a worse editor rather than a broken one.
   */
  const [sources, setSources] = React.useState<RecipientSourceInfo[]>([]);
  const [orphans, setOrphans] = React.useState<string[]>([]);

  // --- UI state ---
  const [filterText, setFilterText] = React.useState('');
  const [overriddenOnly, setOverriddenOnly] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const { perPage, setPerPage } = useTablePreferences('communications', 20);
  const [expandedNames, setExpandedNames] = React.useState<Set<string>>(new Set());
  const [savingName, setSavingName] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<string | null>(null);
  const [historyOf, setHistoryOf] = React.useState<Communication | null>(null);
  const [previewOf, setPreviewOf] = React.useState<Communication | null>(null);
  const [resetting, setResetting] = React.useState<Communication | null>(null);

  /**
   * Leftover overrides, alongside the catalog but never able to fail the page.
   *
   * Not asked for at all unless the user could act on the answer: the endpoint is admin-only, so
   * for everybody else this would be a guaranteed 403 — caught here, but still logged by the
   * browser itself, which puts an error in the console of every non-admin who opens the page.
   */
  const loadOrphans = React.useCallback(async () => {
    if (!isAdmin) {
      setOrphans([]);
      return;
    }
    try {
      setOrphans(await CommunicationApi.orphans());
    } catch {
      setOrphans([]);
    }
  }, [isAdmin]);

  const load = React.useCallback(async () => {
    await Promise.all([retry(), loadOrphans()]);
  }, [retry, loadOrphans]);

  React.useEffect(() => {
    beginLoading();
    void loadOrphans().finally(endLoading);
  }, [loadOrphans]);

  React.useEffect(() => {
    CommunicationApi.recipientSources()
      .then(setSources)
      .catch(err => {
        console.warn('[Communications] Could not read the recipient sources:', err);
        setSources([]);
      });
  }, []);

  const onResetConfirm = async () => {
    const communication = resetting;
    setResetting(null);
    if (!communication) return;

    setSavingName(communication.name);
    try {
      applyWrite(await CommunicationApi.reset(communication.name));
      addNotification(
        'Communication reset',
        AlertVariant.info,
        `${communication.name} is back to the wording, channels and recipients declared in the code.`
      );
    } catch (err) {
      addNotification(`Error resetting ${communication.name}`, AlertVariant.danger, toApiError(err).message);
    } finally {
      setSavingName(null);
    }
  };

  const onRemoveOrphan = async (key: string) => {
    beginLoading();
    try {
      setOrphans(await CommunicationApi.removeOrphan(key));
      addNotification('Orphan removed', AlertVariant.info, `The leftover override for ${key} has been deleted.`);
    } catch (err) {
      addNotification(`Error removing ${key}`, AlertVariant.danger, toApiError(err).message);
    } finally {
      endLoading();
    }
  };

  const toggleExpanded = (name: string) => {
    setExpandedNames(current => {
      const next = new Set(current);
      if (!next.delete(name)) next.add(name);
      return next;
    });
  };

  // --- Filtering & pagination ---
  const filteredRows = React.useMemo(() => {
    const needle = filterText.trim().toLowerCase();
    return communications.filter(communication => {
      if (overriddenOnly && !isOverridden(communication)) return false;
      if (!needle) return true;
      return (
        communication.name.toLowerCase().includes(needle) ||
        communication.description.toLowerCase().includes(needle) ||
        communication.category.toLowerCase().includes(needle) ||
        (communication.subjectTemplate ?? '').toLowerCase().includes(needle) ||
        (communication.pushMessageTemplate ?? '').toLowerCase().includes(needle) ||
        (communication.to ?? '').toLowerCase().includes(needle)
      );
    });
  }, [communications, filterText, overriddenOnly]);

  const paginatedRows = React.useMemo(() => {
    const startIdx = (page - 1) * perPage;
    return filteredRows.slice(startIdx, startIdx + perPage);
  }, [filteredRows, page, perPage]);

  const overriddenCount = React.useMemo(
    () => communications.filter(isOverridden).length, [communications]);

  // Repeated on every communication rather than being a property of one, so any row answers it.
  const emailMasterSwitch = communications[0]?.emailMasterSwitch ?? true;

  /** The load was refused rather than failed: an authenticated user without the admin role, on a
   * service old enough to have had the whole endpoint behind that role. */
  const isForbidden = error?.code === 403;

  /** Kept looked up by name rather than held as an object: a save replaces the communication in
   * the store, and a modal holding the copy it opened with would go on editing the old wording. */
  const editingCommunication = communications.find(c => c.name === editing) ?? null;

  const buildPagination = (variant: 'bottom' | 'top') => (
    <Pagination
      isCompact
      itemCount={filteredRows.length}
      page={page}
      perPage={perPage}
      onSetPage={(_evt, newPage) => setPage(newPage)}
      onPerPageSelect={(_evt, newPerPage, newPage) => { setPerPage(newPerPage); setPage(newPage); }}
      variant={variant as PaginationVariant}
      isStatic
    />
  );

  const clearFilters = () => {
    setFilterText('');
    setOverriddenOnly(false);
    setPage(1);
  };

  return (
    <React.Fragment>
      {/* Header */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Split hasGutter>
          <SplitItem>
            <Title headingLevel="h1" size="4xl">Communications</Title>
          </SplitItem>
          <SplitItem isFilled />
          <SplitItem>
            <ActionList>
              <ActionListItem>
                <PageActionsMenu onRefresh={load} />
              </ActionListItem>
            </ActionList>
          </SplitItem>
        </Split>
        <Content component="p" style={{ marginTop: 'var(--pf-t--global--spacer--sm)' }}>
          Every message the service can send, and what an administrator may change about one without
          a redeploy: whether it happens at all, whether it reaches the browser, whether it is
          mailed, what it says, and who it goes to. A change is live immediately, and within a
          minute on every other replica. Every change is recorded against the user who made it.
        </Content>
        {!isAdmin && (
          <Alert
            isInline
            variant="info"
            title="You are viewing these communications"
            style={{ marginTop: 'var(--pf-t--global--spacer--md)' }}
          >
            Changing one needs the administrator role. Everything here is readable because whether
            a notification was sent at all is answered on this page.
          </Alert>
        )}
        {communications.length > 0 && !emailMasterSwitch && (
          <Alert
            isInline
            variant="warning"
            title="Email is turned off for the whole service"
            style={{ marginTop: 'var(--pf-t--global--spacer--md)' }}
          >
            Nothing below is mailed, whatever an individual communication says. The master switch is
            the notification email setting under Administration &gt; General Settings; it exists so
            that mail can be stopped during an incident without touching every communication.
          </Alert>
        )}
      </PageSection>

      {/* Catalog */}
      <PageSection hasBodyWrapper isWidthLimited>
        <Card>
          <CardBody>
            <Toolbar id="communications-toolbar" clearAllFilters={clearFilters}>
              <ToolbarContent>
                <ToolbarItem>
                  <SearchInput
                    aria-label="Search communications"
                    placeholder="Search by name, wording or recipient..."
                    value={filterText}
                    onChange={(_e, val) => { setFilterText(val); setPage(1); }}
                    onClear={() => { setFilterText(''); setPage(1); }}
                  />
                </ToolbarItem>
                <ToolbarItem>
                  <Checkbox
                    id="communications-overridden-only"
                    label={`Changed only (${overriddenCount})`}
                    isChecked={overriddenOnly}
                    onChange={(_e, checked) => { setOverriddenOnly(checked); setPage(1); }}
                  />
                </ToolbarItem>
                <ToolbarItem variant="pagination">{buildPagination('top')}</ToolbarItem>
              </ToolbarContent>
            </Toolbar>

            {/* Frozen Actions column: the inner container is the horizontal scroll it sticks to, and
                gridBreakPoint="" keeps the table tabular instead of stacking into cards. See app.css. */}
            <OuterScrollContainer style={{ maxHeight: 'calc(100vh - var(--cas-table-offset-lg))', minHeight: 0 }}>
              <InnerScrollContainer>
                <Table aria-label="Communications" isStickyHeader variant="compact" gridBreakPoint="">
                  <Thead>
                    <Tr>
                      <Th screenReaderText="Row expansion" />
                      <Th width={40}>Communication</Th>
                      <Th width={15}>Delivery</Th>
                      <Th width={20}>Mailed to</Th>
                      <Th width={10}>Source</Th>
                      <Th width={10} isStickyColumn stickyMinWidth="150px" stickyRightOffset="0" hasLeftBorder>Actions</Th>
                    </Tr>
                  </Thead>

                  {paginatedRows.map((row, rowIndex) => {
                    const isExpanded = expandedNames.has(row.name);
                    const isSaving = savingName === row.name;

                    return (
                      <Tbody key={row.name} isExpanded={isExpanded}>
                        <Tr>
                          <Td
                            expand={{
                              rowIndex,
                              isExpanded,
                              onToggle: () => toggleExpanded(row.name),
                              expandId: `communication-${row.name}`
                            }}
                          />
                          <Td dataLabel="Communication" modifier="breakWord">
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 'var(--pf-t--global--spacer--sm)'
                              }}>
                                <b>{row.name}</b>
                                <Label isCompact color={SEVERITY_COLORS[row.severity] ?? 'grey'}>
                                  {row.severity}
                                </Label>
                                {row.warnings.length > 0 && (
                                  <Tooltip content={row.warnings.join(' ')}>
                                    <Icon status="warning" aria-label={`${row.warnings.length} warnings`}>
                                      <ExclamationTriangleIcon />
                                    </Icon>
                                  </Tooltip>
                                )}
                              </div>
                              <span style={{ color: 'var(--pf-t--global--text--color--subtle)' }}>
                                {row.description}
                              </span>
                            </div>
                          </Td>
                          <Td dataLabel="Delivery">
                            <DeliveryLabels communication={row} />
                          </Td>
                          <Td dataLabel="Mailed to" modifier="breakWord">
                            <RecipientSummary communication={row} />
                          </Td>
                          <Td dataLabel="Source">
                            <Tooltip
                              content={
                                isOverridden(row)
                                  ? `Changed here by an administrator: ${row.overridden.join(', ')}. `
                                    + 'Everything else still tracks the code.'
                                  : 'Nothing has been changed, so every part of this is the value declared in the code.'
                              }
                            >
                              <Label isCompact color={isOverridden(row) ? 'blue' : 'grey'}>
                                {isOverridden(row) ? `Changed (${row.overridden.length})` : 'Default'}
                              </Label>
                            </Tooltip>
                          </Td>
                          {/* Preview and history are admin-only on the service — the history
                              carries the names of the people who made each change — so for
                              everyone else this column is left out rather than shown leading
                              nowhere. */}
                          <Td dataLabel="Actions" modifier="nowrap"
                            isStickyColumn stickyMinWidth="150px" stickyRightOffset="0" hasLeftBorder>
                            {isAdmin && (
                              <ActionList>
                                <ActionListItem>
                                  <Tooltip content="Edit this communication">
                                    <Button
                                      variant="plain"
                                      aria-label={`Edit ${row.name}`}
                                      icon={<Icon size="lg"><PencilAltIcon /></Icon>}
                                      isDisabled={isSaving}
                                      onClick={() => setEditing(row.name)}
                                    />
                                  </Tooltip>
                                </ActionListItem>
                                <ActionListItem>
                                  <Tooltip content="See what it says, without sending it">
                                    <Button
                                      variant="plain"
                                      aria-label={`Preview ${row.name}`}
                                      icon={<Icon size="lg"><EyeIcon /></Icon>}
                                      onClick={() => setPreviewOf(row)}
                                    />
                                  </Tooltip>
                                </ActionListItem>
                                <ActionListItem>
                                  <Tooltip
                                    content={
                                      isOverridden(row)
                                        ? 'Drop every change and go back to what the code declares'
                                        : 'Nothing to reset — this communication has not been changed'
                                    }
                                  >
                                    <Button
                                      variant="plain"
                                      aria-label={`Reset ${row.name}`}
                                      icon={<Icon size="lg"><RedoAltIcon /></Icon>}
                                      isDisabled={!isOverridden(row) || isSaving}
                                      onClick={() => setResetting(row)}
                                    />
                                  </Tooltip>
                                </ActionListItem>
                                <ActionListItem>
                                  <Tooltip content="Change history">
                                    <Button
                                      variant="plain"
                                      aria-label={`History of ${row.name}`}
                                      icon={<Icon size="lg"><HistoryIcon /></Icon>}
                                      onClick={() => setHistoryOf(row)}
                                    />
                                  </Tooltip>
                                </ActionListItem>
                              </ActionList>
                            )}
                          </Td>
                        </Tr>

                        {isExpanded && (
                          <Tr isExpanded>
                            <Td />
                            <Td colSpan={5}>
                              <ExpandableRowContent>
                                <CommunicationDetail communication={row} />
                              </ExpandableRowContent>
                            </Td>
                          </Tr>
                        )}
                      </Tbody>
                    );
                  })}

                  <Tbody>
                    {/* Reading is open to any authenticated user on a current service, so this is
                        reached only against one old enough to have had the whole endpoint behind
                        the admin role. Worth being specific about: a 403 comes back with an empty
                        body, so the generic failure state would say "an unknown error occurred". */}
                    {isForbidden && (
                      <Tr>
                        <Td colSpan={6}>
                          <Bullseye>
                            <EmptyState
                              status="warning"
                              headingLevel="h4"
                              icon={LockIcon}
                              titleText="You cannot view these communications"
                            >
                              <EmptyStateBody>
                                This service keeps its communications behind the administrator role.
                                Ask an administrator to grant it, or to make the change for you.
                              </EmptyStateBody>
                            </EmptyState>
                          </Bullseye>
                        </Td>
                      </Tr>
                    )}
                    {/* Nothing to show: "the load failed" and "nothing matches" are different answers */}
                    {error && !isForbidden && filteredRows.length === 0 && (
                      <DataLoadErrorRow colSpan={6} error={error} entityLabel="the communications" onRetry={load} />
                    )}
                    {!error && filteredRows.length === 0 && (
                      <Tr>
                        <Td colSpan={6}>
                          <Bullseye>
                            <EmptyState headingLevel="h5" icon={SearchIcon} titleText="No communications found">
                              <EmptyStateBody>
                                {communications.length === 0
                                  ? 'The service reported no communications.'
                                  : 'No communication matches the current filters.'}
                              </EmptyStateBody>
                              {communications.length > 0 && (
                                <EmptyStateFooter>
                                  <EmptyStateActions>
                                    <Button variant="link" onClick={clearFilters}>Clear filters</Button>
                                  </EmptyStateActions>
                                </EmptyStateFooter>
                              )}
                            </EmptyState>
                          </Bullseye>
                        </Td>
                      </Tr>
                    )}
                  </Tbody>
                </Table>
              </InnerScrollContainer>
            </OuterScrollContainer>
            {buildPagination('bottom')}
          </CardBody>
        </Card>
      </PageSection>

      {/* Orphans. Absent entirely when there are none, which is the normal state. */}
      {orphans.length > 0 && (
        <PageSection hasBodyWrapper isWidthLimited>
          <Card>
            <CardBody>
              <Title headingLevel="h2" size="lg">Leftover overrides</Title>
              <Content component="p">
                Changes stored against a communication that no longer exists, left behind by one
                that was renamed or removed. Nothing reads them, so they do no harm — deleting them
                is tidying up.
              </Content>
              <Table aria-label="Leftover communication overrides" variant="compact">
                <Thead>
                  <Tr>
                    <Th>Key</Th>
                    <Th width={10}>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {orphans.map(key => (
                    <Tr key={key}>
                      <Td dataLabel="Key">
                        <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)' }}>{key}</span>
                      </Td>
                      <Td dataLabel="Actions" modifier="nowrap">
                        <RemoveButton
                          confirmationMessage={`Delete the leftover override stored for ${key}?`}
                          onRemoveConfirm={() => void onRemoveOrphan(key)}
                        />
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </CardBody>
          </Card>
        </PageSection>
      )}

      <CommunicationEditModal
        communication={editingCommunication}
        sources={sources}
        onClose={() => setEditing(null)}
      />
      <CommunicationPreviewModal communication={previewOf} onClose={() => setPreviewOf(null)} />
      <CommunicationHistoryModal communication={historyOf} onClose={() => setHistoryOf(null)} />

      {/* Purpose built rather than RemoveConfirmModal: that one ends on "this action cannot be
          undone", which is not true here. A reset drops the override and can be undone by making
          the changes again, and either way the history keeps both. */}
      <Modal variant={ModalVariant.small} isOpen={!!resetting} onClose={() => setResetting(null)} aria-label="Reset communication">
        <ModalHeader title="Reset this communication?" titleIconVariant="warning" />
        <ModalBody>
          {resetting && (
            <Content component="p">
              <b>{resetting.name}</b> goes back to the wording, the channels and the recipients
              declared in the code, dropping {resetting.overridden.length === 1
                ? 'the one change'
                : `all ${resetting.overridden.length} changes`} made here.
            </Content>
          )}
        </ModalBody>
        <ModalFooter>
          <Button key="confirm" variant="primary" onClick={() => void onResetConfirm()}>Reset</Button>
          <Button key="cancel" variant="link" onClick={() => setResetting(null)}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

/**
 * Where one communication ends up.
 *
 * The inbox is unconditional whenever it is raised at all: the row is what the email's deep link
 * points back at, and the claim that makes mail at-most-once is a column on it. So the only honest
 * two states are "off entirely" and "in the inbox, plus whatever else is on".
 */
const DeliveryLabels: React.FunctionComponent<{ communication: Communication }> = ({ communication }) => {
  if (!communication.enabled) {
    return (
      <Tooltip content="Turned off: nothing is written to anybody's inbox, pushed or emailed.">
        <Label isCompact color="red">Off</Label>
      </Tooltip>
    );
  }

  return (
    <LabelGroup numLabels={3}>
      <Tooltip content="Always written while the communication is on. The inbox row is what an email's link points back at.">
        <Label isCompact color="grey" icon={<BellIcon />}>Inbox</Label>
      </Tooltip>
      {communication.push && (
        <Tooltip content="Arrives as a toast while the recipient is signed in.">
          <Label isCompact color="blue">Push</Label>
        </Tooltip>
      )}
      {communication.email && (
        <Tooltip
          content={
            communication.emailMasterSwitch
              ? 'Mailed to the recipients below.'
              : 'Set to be mailed, but email is turned off for the whole service, so nothing is sent.'
          }
        >
          <Label
            isCompact
            color={communication.emailMasterSwitch ? 'green' : 'orange'}
            icon={<EnvelopeIcon />}
          >
            {communication.emailMasterSwitch ? 'Email' : 'Email (blocked)'}
          </Label>
        </Tooltip>
      )}
    </LabelGroup>
  );
};

/** Who the mail goes to, at a glance. Silent about cc and bcc, which the expander shows. */
const RecipientSummary: React.FunctionComponent<{ communication: Communication }> = ({ communication }) => {
  const tokens = parseRecipientList(communication.to);

  if (!communication.email) {
    return (
      <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>
        not emailed
      </span>
    );
  }
  if (tokens.length === 0) {
    return (
      <Tooltip content="Emailed, but addressed to nobody, so no mail is sent.">
        <Label isCompact color="orange" icon={<ExclamationTriangleIcon />}>nobody</Label>
      </Tooltip>
    );
  }

  return (
    <LabelGroup numLabels={3}>
      {tokens.map(token => (
        <Label key={formatRecipientToken(token)} isCompact color="blue">
          {formatRecipientToken(token)}
        </Label>
      ))}
    </LabelGroup>
  );
};

/**
 * The whole of one communication, read only. Behind the expander rather than in the row because it
 * answers a second question — "what exactly does this say, and who else is on it" — that only
 * matters once somebody is looking at one message.
 */
const CommunicationDetail: React.FunctionComponent<{ communication: Communication }> = ({ communication }) => (
  <DescriptionList isHorizontal isCompact columnModifier={{ default: '2Col' }}>
    <DescriptionListGroup>
      <DescriptionListTerm>Subject</DescriptionListTerm>
      <DescriptionListDescription>
        <Mono value={communication.subjectTemplate} />
      </DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Category</DescriptionListTerm>
      <DescriptionListDescription>{communication.category}</DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Message</DescriptionListTerm>
      <DescriptionListDescription>
        {communication.templatable ? (
          <Mono value={communication.pushMessageTemplate} />
        ) : (
          <Tooltip content="Its producers describe genuinely different situations and each supplies its own wording, so there is no template to edit.">
            <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>
              written by whatever raises it
            </span>
          </Tooltip>
        )}
      </DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Seen by</DescriptionListTerm>
      <DescriptionListDescription>
        {/* Deliberately beside the recipients, because they are no longer the same people: a
            facility contact or an external instructor is mailed and has no inbox at all. */}
        <Tooltip content="Who can see the notification in their own inbox — a different question from who is mailed.">
          <span>
            {communication.defaultAudienceScope === 'ALL'
              ? 'every signed-in user'
              : communication.defaultAudienceValue
                ? `${communication.defaultAudienceScope.toLowerCase()}: ${communication.defaultAudienceValue}`
                : communication.defaultAudienceScope.toLowerCase()}
          </span>
        </Tooltip>
      </DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Email body</DescriptionListTerm>
      <DescriptionListDescription>
        <Mono value={communication.emailBodyTemplate} />
      </DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Placeholders</DescriptionListTerm>
      <DescriptionListDescription>
        {communication.placeholders.length === 0 ? (
          <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>none</span>
        ) : (
          <LabelGroup numLabels={8}>
            {communication.placeholders.map(placeholder => (
              <Label key={placeholder} isCompact color="purple">{`{${placeholder}}`}</Label>
            ))}
          </LabelGroup>
        )}
      </DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Cc</DescriptionListTerm>
      <DescriptionListDescription><Mono value={communication.cc} placeholder="nobody" /></DescriptionListDescription>
    </DescriptionListGroup>
    <DescriptionListGroup>
      <DescriptionListTerm>Bcc</DescriptionListTerm>
      <DescriptionListDescription><Mono value={communication.bcc} placeholder="nobody" /></DescriptionListDescription>
    </DescriptionListGroup>
    {communication.warnings.length > 0 && (
      <DescriptionListGroup>
        <DescriptionListTerm>Worth knowing</DescriptionListTerm>
        <DescriptionListDescription>
          {/* Warnings rather than errors: every one of them has a reading under which it is
              exactly what the operator wanted, which is why the service reports rather than
              refuses them. */}
          {communication.warnings.map(warning => (
            <Content component="p" key={warning}>{warning}</Content>
          ))}
        </DescriptionListDescription>
      </DescriptionListGroup>
    )}
  </DescriptionList>
);

const Mono: React.FunctionComponent<{ value: string | null; placeholder?: string }> = ({
  value,
  placeholder = 'none'
}) =>
  value ? (
    <span style={{ fontFamily: 'var(--pf-t--global--font--family--mono)', wordBreak: 'break-word' }}>
      {value}
    </span>
  ) : (
    <span style={{ color: 'var(--pf-t--global--text--color--subtle)', fontStyle: 'italic' }}>
      {placeholder}
    </span>
  );

export { Communications };
